import React      from 'react';
import axios      from 'axios';
import jwt_decode from 'jwt-decode';

import { BACKEND_PATHS }               from 'rest/const';
import CssBaseline                     from '@mui/material/CssBaseline';
import { createRefreshSessionPromise } from 'rest/sendRequest';
import { FINTECH_PATHS }               from 'rest/const';
import { HIDDEN_TABS }                 from 'rest/const';
import { isJWTExpiredError }           from 'rest/sendRequest';
import { OTHER_CONFIG }                from 'rest/const';
import { RouterProvider }              from 'react-router-dom';
import { SWRConfig }                   from 'swr'
import { ThemeProvider }               from '@mui/material/styles';

import isBlank     from './utils/isBlank';
import sendRequest from 'rest/sendRequest';
import useGET      from 'rest/useGET';
import useREST     from 'rest/useREST';

import router from 'routing/router';
import theme  from './theme';

export const AuthContext = React.createContext(null);

/**
 * Refresh session before it expires in order to avoid delays
 */
export const EARLY_REFRESH = 30;
/**
 * Token expires very soon
 */
export const ALMOST_EXPIRED = 5;


function App() {

  const [userInfo, setUserInfo] = React.useState(null);

  const fetcher = useREST();

  useGET(BACKEND_PATHS.configuration, undefined, {
    revalidateIfStale:     false,
    revalidateOnFocus:     false,
    revalidateOnReconnect: false,
    fetcher: fetcher, //SWRConfig doesn't apply here yet
    onSuccess: (res) => {
      if (!res.data) {
        return;
      }
      //TODO once encryption is added we have to decrypt it there first
      axios.defaults.baseURL = res.data.baseUrl;
      axios.defaults.timeout = res.data.frontEndTimeout || 0;

      OTHER_CONFIG.fetchSize = res.data.fetchSize || 50;

      if (!res.data.paths) {
        console.error("Backend didn't provide paths necessary for the application to work properly");
      } else {
        const paths = res.data.paths;
        for (const path in paths) {
          FINTECH_PATHS[path] = paths[path];
        }
      }

      if (!res.data.hide) {
        console.error("Backend didn't provide paths necessary for the application to work properly");
      } else {
        const hide = res.data.hide;
        for (const tab in hide) {
          HIDDEN_TABS[tab] = hide[tab];
        }
      }

      // Trigger re-render in case there are delayed pages with broken paths (happens on docker on chrome)
      if (userInfo) {
        setUserInfo({...userInfo});
      } else {
        setUserInfo(userInfo === null ? undefined : null);
      }
    },
  });

  React.useEffect(
    () => {
      if (!userInfo) {
        return;
      }
      if (userInfo.fromRefreshPromise) { //Login sets cookies, no need to update them
        //This hook triggers each time setUserInfo() is called
        //This way we update cookies only there and not in each place it's called at
        sendRequest(BACKEND_PATHS.updateCookies, 'post', userInfo);
      }
      setAutoRefreshSession(userInfo, setUserInfo);
    }, [userInfo]
  );

  const refreshPromise = userInfo?.refreshPromise; //?. cannot be used in dependency array, so it's set there
  const hasJWTExpired = React.useCallback(
    (error) => {
      if (refreshPromise) { //Session refresh is already in progress
        return true;
      }
      return isJWTExpiredError(error);
    }, [refreshPromise]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline/>
      <SWRConfig
        value={{
          fetcher: fetcher,
        }}
      >
        <AuthContext.Provider value={{ userInfo, setUserInfo, hasJWTExpired }}>
          <RouterProvider router={router}/>
        </AuthContext.Provider>
      </SWRConfig>
    </ThemeProvider>
  );
}

export function getUsernameFromToken(token) {
  if (!token) {
    return undefined;
  }
  if (typeof token === 'string') { //Token is not decoded yet
    token = jwt_decode(token);
  }

  const username = token.admin || token.user; //Currently, it's 1 of those
  if (!isBlank(username)) {
    return username;
  }

  //Just to be safe
  for (const field in token) {
    const val = token[field];
    if (val && val.includes('@') && val.includes('.')) {
      return val;
    }
  }
  return null;
}

export function getSecondsUntilExpiration(token) {
  if (!token) {
    return undefined; //Will return false for operations '>', '<', etc.
  }
  if (typeof token === 'string') { //Token is not decoded yet
    token = jwt_decode(token);
  }
  const currentTimeInMilliseconds = new Date().getTime();
  const currentTimeInSeconds = currentTimeInMilliseconds / 1000;
  //Token's expire time is in seconds
  return Math.floor(token.exp - currentTimeInSeconds);
}

/**
 * Automatically refreshes session before it expires
 */
function setAutoRefreshSession(userInfo, setUserInfo) {
  if (userInfo.refreshPromise) {
    return; //It's already in progress
  }
  const secondsUntilExpires = getSecondsUntilExpiration(userInfo.accessToken);
  if (secondsUntilExpires <= 0) { //Already expired, skip refresh procedure
    return;
  }

  const refreshSessionHandler = createSimpleRefreshSessionPromiseHandler(userInfo, setUserInfo);

  if (secondsUntilExpires <= EARLY_REFRESH) { //Don't need setTimeout(), we refresh right now
    const refreshPromise = createRefreshSessionPromise(userInfo.refreshToken);
    if (secondsUntilExpires <= ALMOST_EXPIRED) { //Very close to expiration
      userInfo.refreshPromise = refreshPromise; //This will prevent sending other request before token is refreshed
    }
    refreshPromise.then(refreshSessionHandler);
    return;
  }

  const timeoutInSeconds = secondsUntilExpires - EARLY_REFRESH;
  const timeoutInMilliseconds = timeoutInSeconds * 1000;
  setTimeout(
    () => {
      if (userInfo.refreshPromise) {
        console.warn('Session refresh is active before token expired?');
        return;
      }
      createRefreshSessionPromise(userInfo.refreshToken)
        .then(refreshSessionHandler);
    },
    timeoutInMilliseconds
  );
}

/**
 * <pre>
 * It will set new userInfo once response returns
 * Called 'simple' to differentiate it from handler in sendRequest
 * </pre>
 * @returns {function(response)} function used in .then()
 */
function createSimpleRefreshSessionPromiseHandler(userInfo, setUserInfo) {
  //Returns function used in .then()
  return (res) => {
    setUserInfo({
      ...userInfo, //Keep username or other values
      ...res.data, //Replace expired tokens with new tokens
      refreshPromise: null, //Do not keep the original promise
      fromRefreshPromise: true, //Will trigger hook to updateCookies
    });
  };
}

export default App;
